import { AxiosInstance } from 'axios/index';
import type { PublicationType } from '@widgets/shared/Publication/types.ts';
import type { ProgramEditorValues } from '@features/programs/index.ts';
import { ActiveTabType } from '@shared/types/common';
import {
  ProgramsData,
  type Program,
  type ProgramAccessDurations,
  type RecommendedProgram,
} from '@shared/types/programs';
import { EntityByIdParam, ModuleParam } from '@src/api/types.ts';

const programsUrl = '/program/';

export class ProgramsApi {
  constructor(private instance: AxiosInstance) {}

  public async getList({ tabName = 'all', ...params }: ProgramsListParams) {
    return this.instance
      .get<ProgramsData>(programsUrl + `${tabName === 'all' ? `${tabName}/` : `all/${tabName}`}`, {
        params,
      })
      .then((res) => res.data);
  }

  public async getById({ id }: EntityByIdParam) {
    return this.instance.get<Program>(programsUrl + `all/${id}/`).then((res) => res.data);
  }

  public async getAccessDurations() {
    return this.instance
      .get<ProgramAccessDurations[]>(programsUrl + `access_durations//`)
      .then((res) => res.data);
  }

  public async getRecommended({ hash }: GetRecommendedProgramParams) {
    return this.instance
      .get<RecommendedProgram>(programsUrl + `all/${hash}/subscribe`)
      .then((res) => res.data);
  }

  public async update({ programData, id }: UpdateProgramDto) {
    return this.instance.patch<Program>(programsUrl + `all/${id}/`, programData);
  }

  public async updatePublication({ publicationData, id }: UpdateProgramPublicationDto) {
    return this.instance.patch<PublicationType>(
      programsUrl + `all/${id}/publication/`,
      publicationData
    );
  }

  public async delete({ id }: EntityByIdParam) {
    return this.instance.delete<void>(programsUrl + `all/${id}/`);
  }

  public async makeFavorite({ id }: EntityByIdParam) {
    return this.instance.post<void>(programsUrl + `/favorite/`, { pk: id });
  }
  public async deleteFromFavorite({ id }: EntityByIdParam) {
    return this.instance.delete<void>(programsUrl + `/favorite/${id}/`);
  }

  public async like({ id }: EntityByIdParam) {
    return this.instance.post<void>(programsUrl + `all/${id}/like/`);
  }

  public async removeLike({ id }: EntityByIdParam) {
    return this.instance.delete<void>(programsUrl + `all/${id}/like/`);
  }

  public async getHistory(params: ProgramsHistoryListParams) {
    return this.instance
      .get<ProgramsData>(programsUrl + `all/view_history/`, { params })
      .then((res) => res.data);
  }
}

export type UpdateProgramDto = { programData: FormData | Partial<ProgramEditorValues>; id: number };
export type UpdateProgramPublicationDto = { publicationData: Partial<PublicationType>; id: number };
export type GetRecommendedProgramParams = {
  hash?: string;
};
export type ProgramsHistoryListParams = Partial<
  ModuleParam & {
    tags: string;
    authors: string;
    search: string;
    page: number;
  }
>;

export type ProgramsListParams = ProgramsHistoryListParams & { tabName?: ActiveTabType };
